import { getFundingInstrumentOptions, RedditLevel } from 'wb-reddit/app/routes/superGrid/utils';
import {
	AdStatus,
	CampaignObjective, CampaignSpecialAdCategory,
	CampaignStatus, CampaignSuperGridListQueryItems, CreateCreativeContentCallToAction,
	CreativeType,
	GroupBidStrategy,
	GroupGoal,
	GroupStatus, GroupSuperGridListQueryItems, GroupTargetingLocations,
} from 'wb-reddit/graphql/generated';
import dayjs from 'dayjs';
import {
	GroupAudienceTypeOptions,
} from 'wb-reddit/app/routes/superGrid/columnDefinitions/group';
import { DATE_TIME_ISO_FORMAT } from '@aleph/shared-components';
import { getLevelByDepth, getLevelDepth } from '@aleph/supergrid';
import { REDDIT_SUPERGRID_CONFIG } from 'wb-reddit/app/routes/superGrid/config';
import { getGroupBidTypeForObjective } from 'wb-reddit/app/routes/superGrid/mutator/hooks/utils';

export enum RedditCreateLevels {
	CAMPAIGN = RedditLevel.CAMPAIGN,
	GROUP = RedditLevel.GROUP,
	AD = RedditLevel.AD,
}

export interface RedditSuperGridMutationsConfig {
	data: {
		[RedditCreateLevels.CAMPAIGN]: CampaignSuperGridListQueryItems & { isExistingCreate: boolean }
		[RedditCreateLevels.GROUP]: GroupSuperGridListQueryItems & { isExistingCreate: boolean }
		[RedditCreateLevels.AD]: GroupSuperGridListQueryItems & { isExistingCreate: boolean }
	}
	levelOrder: [RedditCreateLevels.CAMPAIGN, RedditCreateLevels.GROUP, RedditCreateLevels.AD]
}

// todo: replace with types from graphql when backend is ready
export type RedditCreateData = Record<string, any>;

/**
 * This is a map of dependencies between levels.
 * It is used to update values of a child entity when a specific field on parent entity is changed.
 * You can return as many fields as you want, they will be merged with the existing values.
 *
 * For changing values on the same level you should still use valueSetter
 */
export const REDDIT_CROSS_LEVEL_DEPENDENCIES = {
	campaignObjective: (newValue) => {
		switch (newValue) {
			case CampaignObjective.Impressions: // BRAND AWARENESS AND REACH
				return {
					groupAppId:       undefined,
					groupBidStrategy: GroupBidStrategy.ManualBidding,
					groupBidAmount:   3500000, // 3.5
					placements:       [
						{ value: GroupTargetingLocations.Feed, label: 'Feed' },
					],
					creativeType: CreativeType.Image,
					deviceType:   [],
				};
			case CampaignObjective.VideoViewableImpressions: // VIEW VIEWS
				return {
					groupAppId:       undefined,
					groupBidStrategy: GroupBidStrategy.ManualBidding,
					groupBidAmount:   100000, // 0.1
					placements:       [
						{ value: GroupTargetingLocations.Feed, label: 'Feed' },
					],
					creativeType: CreativeType.Video,
					deviceType:   [],
				};
			case CampaignObjective.Clicks: // TRAFFIC
				return {
					groupBidStrategy: GroupBidStrategy.MaximizeVolume,
					groupBidAmount:   100000, // 0.1
					creativeType:     CreativeType.Image,
					deviceType:       [],
				};
			case CampaignObjective.Conversions:
				return {
					groupAppId:       undefined,
					groupBidStrategy: GroupBidStrategy.MaximizeVolume,
					groupBidAmount:   100000, // 0.1
					placements:       [
						{ value: GroupTargetingLocations.Feed, label: 'Feed' },
					],
					creativeType: CreativeType.Image,
					deviceType:   [],
				};
			case CampaignObjective.AppInstalls:
				return {
					groupBidStrategy: GroupBidStrategy.MaximizeVolume,
					groupBidAmount:   100000, // 0.1
					placements:       [
						{ value: GroupTargetingLocations.Feed, label: 'Feed' },
					],
					creativeType: CreativeType.Image,
				};
		}

		return {};
	},
};

export const REDDIT_DEFAULT_CREATE_VALUES = {
	[RedditCreateLevels.CAMPAIGN]: (parentData: RedditCreateData) => ({
		campaignStatus:              CampaignStatus.Active,
		campaignObjective:           CampaignObjective.Impressions,
		campaignSpecialAdCategories: [CampaignSpecialAdCategory.None],
		isInCreation:                true,
		campaignSpendCap:            null,
		fundingInstrument: 	         getFundingInstrumentOptions(parentData)?.[0]?.value ?? null,
	}),
	[RedditCreateLevels.GROUP]: (parentData: RedditCreateData) => ({
		groupStatus:      GroupStatus.Active,
		groupBidType:     getGroupBidTypeForObjective(parentData?.campaignObjective),
		groupBidStrategy: parentData?.campaignObjective === CampaignObjective.Impressions
			|| parentData?.campaignObjective === CampaignObjective.VideoViewableImpressions
			? GroupBidStrategy.ManualBidding
			: GroupBidStrategy.MaximizeVolume,
		groupBidAmount:          5000000,
		groupGoal:               GroupGoal.DailySpend,
		groupGoalAmount:         10000000 * 30,
		groupStartTime:          dayjs().format(DATE_TIME_ISO_FORMAT),
		groupEndTime:            null,
		audienceType:            GroupAudienceTypeOptions[0].value,
		placements:              [{ value: GroupTargetingLocations.Feed, label: 'Feed' }],
		communities:             [],
		communitiesExcluded:     [],
		keywords:                [],
		locations:               [],
		locationsExcluded:       [],
		customAudiences:         [],
		customAudiencesExcluded: [],
		interests:               [],
		devices:                 [],
		carriers:                [],
		isInCreation:            true,
	}),
	[RedditCreateLevels.AD]: (parentData: RedditCreateData) => ({
		adName:           '',
		adStatus:         AdStatus.Active,
		creativeHeadline: 'Headline text',
		creativeType:     parentData?.parent?.campaignObjective === CampaignObjective.VideoViewableImpressions
			? CreativeType.Video
			: CreativeType.Image,
		profile:                      parentData?.parent?.parent?.adAccount?.profiles?.[0],
		creativeAllowComments:        false,
		creativeContentCallToAction0: CreateCreativeContentCallToAction.Download,
		isInCreation:                 true,
	}),
};

export const getRedditParentLevel = (level: RedditLevel): RedditLevel => getLevelByDepth(
	REDDIT_SUPERGRID_CONFIG,
	getLevelDepth(REDDIT_SUPERGRID_CONFIG, level) - 1,
) as RedditLevel;
